import React, { useEffect, useMemo, useRef, useState } from 'react';
import cssClass from 'classnames';
import {
  FlyoutDrawerComponent,
  GlobalStyle,
  StyledContainer,
  StyledModalContainer,
  StyledModalHeader,
} from './FlyoutDrawer.styles';
import { eventUtil } from '../../utils/eventUtil';
import { canUseDOM } from '../../utils';
import { useScreenWidth } from '../../utils/useScreenWidth';
import { constants } from '../../constants/constants';
import { CustomModal } from '../CustomModal';
import { Container } from '@adobe/aem-react-editable-components';
import { FlyoutDrawerProps, DrawerDataProps } from './FlyoutDrawer.types';
import { useFocusTrap } from './hooks/useFocusTrap';

const { MAX_VIEWPORT_MOBILE, ENTER, SPACE, NRP_RLM_FLYOUT_ID, NRP_OVERFLOW_TIMEOUT } = constants;

export const FlyoutDrawer: React.FC<FlyoutDrawerProps> = props => {
  const [isOpen, setOpen] = useState(false);
  const hasBeenOpenedRef = useRef(false);
  const [eventData, setEventData] = useState({});
  const [contentWrapperCls, setContentWrapperCls] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const screenWidth = useScreenWidth();
  const closeButtonTxt = canUseDOM ? window?.commonPageContent?.['closeButton'] : '';
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useFocusTrap(isOpen, panelRef);

  // Function to handle closing the panel
  const handlePanelClose = () => {
    // prevent close if signIn model and NRP flyout are open
    const accountModelElArray = Array.from(document.getElementsByClassName('account-container-modal'));
    const isSignInModelOpen = Array.from(accountModelElArray).some(el => el.classList.contains('show'));
    if (!(isSignInModelOpen && props.id === NRP_RLM_FLYOUT_ID)) {
      // Clear any existing timeout before setting a new one
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      eventUtil.dispatch('CLOSE_DRAWER', {}); // Dispatch only when drawer actually closes
    }
  };

  // Component handles escape key at document level
  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handlePanelClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleDocumentKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handlePanelCloseKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    // Close button should also work with keypress.
    // <span> is a non-interactive and non-focusable element by default.
    // Browsers do not associate keyboard input with a click on <span> elements unless we manually implement that behavior.
    if (e.key === ENTER || e.key === SPACE) {
      e.preventDefault(); // Prevent scroll for space
      handlePanelClose();
    }
  };

  // Function to handle clicks outside the panel
  const handleClickOutside = (event: MouseEvent) => {
    if (panelRef?.current && isOpen && !panelRef?.current?.contains(event.target as Node)) {
      handlePanelClose();
    }
  };

  // Function to handle keydown events on component level (backup)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      handlePanelClose();
    }
  };

  // useEffect to add and remove click event listener for handling clicks outside the panel
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  const removeOverflow = () => {
    closeTimeoutRef.current = setTimeout(() => {
      document?.body?.style?.removeProperty('overflow');
    }, NRP_OVERFLOW_TIMEOUT);
  };

  // useEffect to handle opening and closing the drawer based on events
  useEffect(() => {
    const handleOpenDrawer = (data: DrawerDataProps) => {
      const { flyoutCompId, contentWrapperClass } = data;
      if (props?.id === flyoutCompId) {
        hasBeenOpenedRef.current = true;
        setOpen(true);
        setEventData(data);
        if (contentWrapperClass) {
          setContentWrapperCls(contentWrapperClass);
        }
        document.body.style.overflow = 'hidden';
      }
    };

    const handleCloseDrawer = () => {
      setOpen(false);
      removeOverflow();
    };

    eventUtil.on('OPEN_DRAWER', handleOpenDrawer);
    eventUtil.on('CLOSE_DRAWER', handleCloseDrawer);

    return () => {
      eventUtil.remove('OPEN_DRAWER', handleOpenDrawer);
      eventUtil.remove('CLOSE_DRAWER', handleCloseDrawer);
    };
  }, [props.id]);

  const { model, cqItems } = useMemo(() => {
    const tempModal = props.model;
    const cqItems = tempModal?.cqItems || {};
    const cqItemsArr = Object.keys(cqItems);
    cqItemsArr.forEach(item => {
      cqItems[item] = { ...cqItems[item], eventData };
    });
    return {
      model: {
        ...tempModal,
        cqItems,
      },
      cqItems,
    };
  }, [props.model, eventData]);

  const Header = ({ isCloseIcon }: { isCloseIcon: boolean }) => {
    const { iconFontPath, heading } = props;
    return (
      <div className={cssClass('header-container')}>
        {iconFontPath && <span className={cssClass('header-icon', iconFontPath)}></span>}
        {heading && <span className="text-wrapper">{heading}</span>}
        {isCloseIcon && (
          <span
            className="close-icon icon-clear custom_click_track"
            tabIndex={0}
            role="button"
            onClick={handlePanelClose}
            onKeyDown={handlePanelCloseKeyDown}
            aria-label={closeButtonTxt}
            data-testid="close-icon"
          ></span>
        )}
      </div>
    );
  };

  return screenWidth > MAX_VIEWPORT_MOBILE ? (
    <FlyoutDrawerComponent isOpen={isOpen} onKeyDown={handleKeyDown} className="standard">
      <StyledContainer
        isOpen={isOpen}
        hasBeenOpened={hasBeenOpenedRef.current}
        ref={panelRef}
        role="dialog"
        aria-hidden={!isOpen ? 'true' : undefined}
        aria-modal={isOpen ? 'true' : undefined}
        aria-label={props?.id || ''}
      >
        <Header isCloseIcon />
        <div className={cssClass('content-wrapper', contentWrapperCls)}>
          <Container
            isInEditor={true}
            {...props}
            {...props?.model}
            isPage={false}
            model={model}
            cqItems={cqItems}
            className="d-flex flex-column height-100"
          />
        </div>
      </StyledContainer>
      <GlobalStyle />
    </FlyoutDrawerComponent>
  ) : (
    <CustomModal
      openModal={Boolean(isOpen)}
      handleClose={handlePanelClose}
      optionsModal={false}
      styledClassName={'customize-flyout-header'}
      handleBackButton={handlePanelClose}
      children={
        <StyledModalContainer className={cssClass(contentWrapperCls)}>
          <Container
            isInEditor={true}
            {...props}
            {...props?.model}
            isPage={false}
            model={model}
            cqItems={cqItems}
            className="d-flex flex-column height-100"
          />
        </StyledModalContainer>
      }
      heading={
        <StyledModalHeader>
          <Header isCloseIcon={false} />
        </StyledModalHeader>
      }
    />
  );
};
